import { getXPromo, initialize, IUserData, switchGame } from '@sgorg/x-promo-game-client';
import DateHelper from '../DateHelper';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import Game from '../../Game';
import {isIOS, IsMobile, Platform } from '../../Utility/Platform';
import CrossPromoCampaign from './CrossPromoCampaign';
import { WorldDataHelper } from '../WorldDataHelper';
import { IsDesktop } from '../Platform';
import { TimerType } from '../../System/Timer/TimerTypes';
import { IGameState } from '../../State/Game/IGameState';
import { CrossPromoStatus } from '../../State/Game/IUserState';
import { report } from '../../report';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { DEBUG_X_PROMO_CAMPAIGNS } from 'Constants/CrossPromo';

export enum CrossPromoAssetType {
    ICON = 'icon',
    PORTRAIT_IMAGE = 'portrait',
    LANDSCAPE_IMAGE = 'landscape',
    SQUARE_IMAGE = 'square image',
    VIDEO = 'video',
}

export enum CrossPromoPlacement {
    ICON_PICTURE_COMBO = 'icon_picture_combo',
    NO_COINS = 'no_lives_flow',
    AT_LOGIN = 'at_login_image',
    NO_REWARDED_ADS = 'no_rewarded_ads',
}
export interface ICrossPromoAssetData {
    language: string;
    type: CrossPromoAssetType;
    src: string;
}

export const CrossPromoPlacementToAssetType: Map<CrossPromoPlacement, CrossPromoAssetType> = new Map<
    CrossPromoPlacement,
    CrossPromoAssetType
>([
    [CrossPromoPlacement.AT_LOGIN, CrossPromoAssetType.LANDSCAPE_IMAGE],
    [CrossPromoPlacement.NO_COINS, CrossPromoAssetType.LANDSCAPE_IMAGE],
    [CrossPromoPlacement.ICON_PICTURE_COMBO, CrossPromoAssetType.LANDSCAPE_IMAGE],
]);
const gameSwitch = (data: { targetGameId: string; payload: any }) => {
    Game.sdk.switchGame(data.targetGameId, data.payload).catch(error => console.log(error));
};
const tracking = (data: { eventName: FBTrackingEvent; params: any }) => {
    Game.sdk.gameTracking(data.eventName, 1, data.params);
};
export default class CrossPromoDriver {
    public static async getCrossPromoStatus(): Promise<CrossPromoStatus> {
        if (isIOS() || Game.config.platform !== Platform.FACEBOOK) return CrossPromoStatus.DISABLED;

        const userData: IUserData = await this.getCrossPromoUserData();
        let campaigns = undefined;
        try {
            campaigns = await getXPromo({
                userData,
                serverTime: DateHelper.now,
            }).catch(e => {
                console.warn('Could not get cross promo status', e);
                return undefined;
            });
        } catch (e) {
            console.warn('Could not get cross promo status', e);
            return CrossPromoStatus.DISABLED;
        }
        return campaigns === undefined ? CrossPromoStatus.DISABLED : CrossPromoStatus.ENABLED;
    }
    public static async init(): Promise<void> {
        if(Game.config.platform !== Platform.FACEBOOK) return;

        const userData: IUserData = await this.getCrossPromoUserData();
        initialize({
            gameId: Game.config.gameID,
            playerId: Game.sdk.getUserId(),
            switchFunc: gameSwitch,
            trackingFunc: tracking,
            payload: Game.sdk.getEntryPointData(),
            userData,
        });
    }

    public static async getCrossPromoUserData(state: IGameState = Game.store.getState()): Promise<IUserData> {
        const initialLoginTime = state.playerState.timers.get(TimerType.INITIAL_LOGIN);
        const fullDaysSince = (date: Date) => Math.floor(Math.abs(DateHelper.timeSince(date)) / DateHelper.Day);
        const userLifetime: number = initialLoginTime === null ? 0 : fullDaysSince(initialLoginTime);
        const lastPurchaseDate = state.playerState.timers.get(TimerType.LAST_PURCHASE);
        const timeSinceLastPayment: number = lastPurchaseDate ? fullDaysSince(lastPurchaseDate) : null;

        const result: IUserData = {
            userLifetime,
            userLanguage: Game.sdk.getLanguage(),
            sessionOfDay: state.playerState.sessionCount,
            gameBotSubscribed: state.playerState.botOptIn,
            numFriendsInApp: state.playerState.social.friendCount,
            score: ProgressStateHelper.getStarsCount(state.playerState),
            highestLevel: Game.player.getHighestLevelCompleted(state),
            payer: state.playerState.purchases > 0,
            payerLastPurchaseDays: timeSinceLastPayment,
            payerTotalPurchases: state.playerState.purchases,
            device: IsMobile() ? 'mobile' : IsDesktop ? 'desktop' : 'other',
            originGameId: Game.config.gameID,
            originPlayerId: Game.sdk.getUserId(),
            endOfContent: Game.player.getHighestLevelCompleted(state) >= this.maxLevel,
        };

        return result;
    }
    public static async getCampaigns(state: IGameState = Game.store.getState()): Promise<CrossPromoCampaign[]> {
        const userData: IUserData = await CrossPromoDriver.getCrossPromoUserData(state).catch(e => {
            console.warn('Could not get cross promo status', e);
            return undefined;
        });

        if (userData === undefined) return [];

        if (state.perSessionData.debugXPromo) {
            return this.mapCampaignData(DEBUG_X_PROMO_CAMPAIGNS);
        }

        return await getXPromo({
            userData,
            serverTime: DateHelper.now,
        }).then(this.mapCampaignData, e => {
            console.warn('Could not get cross promo status', e);
            return [];
        });
    }
    public static async getCampaignOfType(
        type: CrossPromoPlacement,
        state: IGameState = Game.store.getState()
    ): Promise<CrossPromoCampaign> {
        let campaigns: CrossPromoCampaign[];
        try {
            campaigns = await this.getCampaigns();
        } catch (error) {
            report('Failed to get XPromo Campaigns', {
                error,
                once: true,
                tags: { campaignType: type },
            });
        }

        if (campaigns === undefined) {
            return undefined;
        }

        const campaignOfType: CrossPromoCampaign = campaigns.find(
            (campaign: CrossPromoCampaign) => campaign.placement === type
        );

        return campaignOfType;
    }

    public static switchGame(campaignId: number): void {
        switchGame(campaignId);
    }
    private static mapCampaignData(campaigns: any[]): CrossPromoCampaign[] {
        return campaigns.map((campaign: any) => new CrossPromoCampaign(campaign));
    }
    private static get maxLevel(): number {
        if (!this._maxLevel) {
            this._maxLevel = WorldDataHelper.maxLevel();
        }
        return this._maxLevel;
    }
    private static _maxLevel: number;
}
